import Calendar from '@/components/calendar/Calendar';
import { Button } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/Select';
import Table from '@/components/table/Table';
import { DEFAULT_SELECT_PAGE_SIZES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { formatDateTime } from '@/utils/datetime';
import { yupResolver } from '@hookform/resolvers/yup';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import { Ban, Check, Edit, EllipsisIcon, PlusIcon, SearchIcon, Trash2Icon } from 'lucide-react';
import { flowResult, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AddReferralCodeModal from './components/FormReferralCodeModal';
import { scrollToTop } from '@/utils/utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/common/DropdownMenu';
import { ActionDropdownItem } from '@/components/general/ActionDropdownItem';
import { toastify } from '@/utils/toastify';
import ViewDetailReferralCodeModal from './components/ViewDetailReferralCodeModal';

const STATUS_REFERRAL_CODES = {
    ACTIVE: 'active',
    IN_ACTIVE: 'in_active',
    EXPIRED: 'expired'
};

export default observer(function ReferralCodesPages() {
    const { t, i18n: { language } } = useTranslation();
    const navigate = useNavigate();

    const {
        modalStore: { showAlertModal, showModal, hideModal },
        referralCodeStore: { paging, totalRecord, referralCodes, getAllReferralCodes, setObservable, keepSearchParams, defaultPaging }
    } = useStore();

    const validateSchema = yup.object().shape({
        referralCode: yup.string(),
        organizationName: yup.string(),
        status: yup.string(),
        startTime: yup.date().nullable(),
        endTime: yup.date().nullable()
    });

    const [searchState, setSearchState] = useState<Object>(keepSearchParams ?? '');
    // Giá trị time search để truyền vào modal view referral code
    const [timeRange, setTimeRange] = useState<{ startTime: Date | null, endTime: Date | null }>({
        startTime: null,
        endTime: null
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const initialValues = {
        referralCode: '',
        organizationName: '',
        status: 'all',
        startTime: null,
        endTime: null
    };

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: initialValues
    });

    const watchStartTime = form.watch('startTime');
    const watchEndTime = form.watch('endTime');

    const columns: ColumnDef<any>[] = [
        {
            header: t('words_title.index'),
            size: 10,
            enableSorting: false,
            cell: ({ row: { index } }) => (
                <div>{paging.pagination.pageIndex * paging.pagination.pageSize + index + 1}</div>
            )
        },
        {
            accessorKey: 'code',
            header: t('words_title.referral_table_referral_code'),
            enableSorting: false,
            size: 150,
            cell: ({ row: { original } }) => (
                <>
                    <div>{original?.code}</div>
                </>
            )
        },
        {
            accessorKey: 'organizationName',
            header: t('words_title.referral_table_organization_name'),
            enableSorting: false,
            size: 250,
            cell: ({ row: { original } }) => (
                <>
                    <div>{original?.organizationName}</div>
                </>
            )
        },
        {
            accessorKey: 'expiredDate',
            header: t('words_title.referral_table_expired_date'),
            enableSorting: false,
            size: 100,
            cell: ({ row: { original } }) => (
                <>
                    <div>{original?.expiredDate ? formatDateTime(original.expiredDate, 'DD/MM/YYYY') : '--'}</div>
                </>
            )
        },
        {
            accessorKey: 'number_of_use',
            header: t('words_title.referral_table_number_of_use'),
            enableSorting: true,
            size: 120,
            cell: ({ row: { original } }) => (
                <>
                    <div className='text-center '>
                        {
                            Number(original?.numberOfUse) > 0 ?
                                (
                                    <span
                                        className='text-[#0096F3] underline cursor-pointer'
                                        onClick={() => {
                                            showPopupViewDetail(original);
                                        }}
                                    >
                                        {original?.numberOfUse}
                                    </span>
                                ) :
                                (
                                    <span>{original?.numberOfUse}</span>
                                )
                        }
                    </div>
                </>
            )
        },
        {
            accessorKey: 'createdAt',
            header: t('words_title.referral_table_created_date'),
            enableSorting: false,
            size: 150,
            cell: ({ row: { original } }) => (
                <>
                    <div>{original.createdAt ? formatDateTime(original.createdAt, 'DD/MM/YYYY  hh:mm A') : '--'}</div>
                </>
            )
        },
        {
            accessorKey: 'status',
            header: t('words_title.referral_table_status'),
            enableSorting: false,
            size: 100,
            cell: ({ row: { original } }) => (
                <>
                    <div>{getPromotionStatusLabel(original, t)}</div>
                </>
            )
        },
        {
            id: 'actions',
            size: 10,
            cell: ({ row: { original } }) => (
                <div className='flex justify-center items-center'>
                    <ActionMenuDropdown original={original} loadDataTable={loadDataTable} status={original?.statusLabel} />
                </div>
            )
        }
    ];

    useEffect(() => {
        loadDataTable();
    }, []);

    const loadDataTable = async () => {
        await flowResult(getAllReferralCodes(searchState, paging));
    };
    const onFetch = (searchParams?: any, paging?: TableState) => {
        if (paging) {
            setObservable('paging', paging ?? null);
        }

        setObservable('keepSearchParams', toJS(searchParams));
        getAllReferralCodes(searchParams, paging);
    };

    const onSearch = (searchParams: FormData) => {
        setTimeRange({
            startTime: watchStartTime ?? null,
            endTime: watchEndTime ?? null
        });
        setSearchState(searchParams);
        onFetch(searchParams, defaultPaging);
    };

    const handleClickAddPromotion = () => {
        showModal({
            size: '4xl',
            title: t('words_title.referral_modal_title'),
            content: <AddReferralCodeModal reLoadData={loadDataTable} modeForm='create' />,
            footerClassName: 'hidden'
        });
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging = stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        onFetch(searchState, newPaging);
        scrollToTop();
    };

    const handleDisableTime = (date: Date, limitDate: string | Date | undefined | any, isToDate: boolean) => {
        if (limitDate) {
            return isToDate ? date < limitDate : date > limitDate;
        }
        return false;
    };

    const showPopupViewDetail = (original) => {
        showModal({
            size: '4xl',
            title: t('words_title.referral_detail_title'),
            content: <ViewDetailReferralCodeModal id={original.id} startTimeProp={timeRange.startTime} endTimeProp={timeRange.endTime} />,
            footerClassName: 'hidden'
        });
    };

    return (
        <>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSearch)}>
                    <div className='bg-white rounded p-4'>
                        <div className='grid grid-cols-2 gap-x-20 gap-y-4'>
                            <FormField
                                control={form.control}
                                name='referralCode'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/5 label-1'>{t('words_title.referral_referral_code')}</Label>
                                            <FormControl className='w-4/5'>
                                                <Input type='text' {...field} />
                                            </FormControl>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name='organizationName'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/5 label-1'>{t('words_title.referral_organization_name')}</Label>
                                            <FormControl className='w-4/5'>
                                                <Input type='text' {...field} />
                                            </FormControl>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='status'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center'>
                                            <Label className='w-1/5 label-1'>{t('words_title.referral_status')}</Label>
                                            <Select
                                                key={field.value}
                                                onValueChange={field.onChange}
                                                {...field}
                                                className='w-4/5'
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder={t('placeholder.choose')} />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem
                                                        className='hover:cursor-pointer'
                                                        value='all'
                                                    >
                                                        <span>{t('words_title.all')}</span>
                                                    </SelectItem>
                                                    <SelectItem
                                                        className='hover:cursor-pointer'
                                                        value={STATUS_REFERRAL_CODES.ACTIVE}
                                                    >
                                                        <span>{t('words_title.promotion_status_active')}</span>
                                                    </SelectItem>
                                                    <SelectItem
                                                        className='hover:cursor-pointer'
                                                        value={STATUS_REFERRAL_CODES.IN_ACTIVE}
                                                    >
                                                        <span>{t('words_title.promotion_status_inactive')}</span>
                                                    </SelectItem>
                                                    <SelectItem
                                                        className='hover:cursor-pointer'
                                                        value={STATUS_REFERRAL_CODES.EXPIRED}
                                                    >
                                                        <span>{t('words_title.promotion_status_expired')}</span>
                                                    </SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className='flex items-start'>
                                <Label className='w-1/5 label-1 h-[48px] leading-[48px]'>{t('words_title.referral_status_period')}</Label>
                                <div className='flex items-center gap-3 w-4/5'>
                                    <FormField
                                        control={form.control}
                                        name='startTime'
                                        render={({ field }) => (
                                            <FormItem className='w-1/2'>
                                                <FormControl>
                                                    <Calendar
                                                        mode='single'
                                                        selected={field.value}
                                                        onSelect={field.onChange}
                                                        disabled={date => handleDisableTime(date, watchEndTime, false)}
                                                        useIconRemove={true}
                                                        className='w-full'
                                                        placeholder={t('placeholder.from')}
                                                        required
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                    <div> - </div>
                                    <FormField
                                        control={form.control}
                                        name='endTime'
                                        render={({ field }) => (
                                            <FormItem className='w-1/2'>
                                                <FormControl>
                                                    <Calendar
                                                        mode='single'
                                                        selected={field.value}
                                                        onSelect={field.onChange}
                                                        disabled={date => handleDisableTime(date, watchStartTime, true)}
                                                        useIconRemove={true}
                                                        className='w-full'
                                                        placeholder={t('placeholder.to')}
                                                        required
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className='flex justify-center mt-8'>
                            <Button variant='outline' className='min-w-48 flex items-center justify-center gap-2 group'>
                                <SearchIcon className='w-5 h-5 text-active group-hover:text-white' />
                                <span>{t('button.search')}</span>
                            </Button>
                        </div>
                    </div>
                </form>
            </Form>
            <div className='bg-[#fff] p-4 mt-4 rounded'>
                <Button type='button' variant='default' className='text-white flex items-center justify-center gap-2 min-w-28 ml-auto' onClick={handleClickAddPromotion}>
                    <PlusIcon className='w-7 h-7 text-white' />
                    <span>{t('words_title.referral_table_add_referral_code')}</span>
                </Button>
                <Table
                    columns={columns}
                    data={referralCodes}
                    rowCount={totalRecord}
                    onStateChange={onStateChange}
                    state={paging}
                    selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                    getRowId={row => row.id?.toString()}
                />
            </div>
        </>
    );
});

const getPromotionStatusLabel = (original: any, t: any) => {
    if (original?.statusLabel === STATUS_REFERRAL_CODES.EXPIRED) {
        return <div className='border-[1px] border-[#E40031] rounded-[16px] p-1 text-[#E40031] text-center min-w-[130px] max-w-[160px]'>{t('words_title.promotion_status_expired')}</div>;
    }

    if (original?.statusLabel === STATUS_REFERRAL_CODES.ACTIVE) {
        return <div className='border-[1px] border-[#009F46] rounded-[16px] p-1 text-[#009F46] text-center min-w-[130px] max-w-[160px]'>{t('words_title.promotion_status_active')}</div>;
    }

    if (original?.statusLabel === STATUS_REFERRAL_CODES.IN_ACTIVE) {
        return <div className='border-[1px] border-[#7D7D7D] rounded-[16px] p-1 text-[#7D7D7D] text-center min-w-[130px] max-w-[160px]'>{t('words_title.promotion_status_inactive')}</div>;
    }

    return <div>--</div>;
};

interface ActionMenuDropdownProps {
    status: string,
    loadDataTable: () => void,
    original: any
}

const ActionMenuDropdown = observer(({ original, loadDataTable, status }: ActionMenuDropdownProps) => {
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();

    const {
        modalStore: { showModal, showAlertModal, modalWaringAlertOptions, hideModal },
        referralCodeStore: { deleteReferralCode, changeStatusReferralCode }
    } = useStore();

    // // function
    const handleClickInActive = async () => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            size: 'lg',
            content: t('messages.confirm_inactive_referral_code'),
            onSave: async () => {
                const res = await flowResult(changeStatusReferralCode(original.id, { status: false }));
                if (res && res.ok) {
                    loadDataTable();
                    toastify('success', t('messages.update_success'));
                }
            }
        }));
    };

    const handleClickActive = async () => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            size: 'lg',
            content: t('messages.confirm_active_referral_code'),
            onSave: async () => {
                const res = await flowResult(changeStatusReferralCode(original.id, { status: true }));
                if (res && res.ok) {
                    loadDataTable();
                    toastify('success', t('messages.update_success'));
                }
            }
        }));
    };

    const handleClickDelete = async () => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            size: 'lg',
            content: t('messages.confirm_delete_referral_code'),
            onSave: async () => {
                const res = await flowResult(deleteReferralCode(original?.id));
                if (res) {
                    toastify('success', t('messages.update_success'));
                    loadDataTable();
                }
            }
        }));
    };

    const handleClickEdit = async () => {
        showModal({
            size: '4xl',
            title: t('words_title.referral_edit_modal_title'),
            content: <AddReferralCodeModal reLoadData={loadDataTable} modeForm='edit' id={original.id} />,
            footerClassName: 'hidden'
        });
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <EllipsisIcon className='h-6 w-6 text-active hover:cursor-pointer' />
            </DropdownMenuTrigger>

            <DropdownMenuContent className='w-48' align='end' forceMount>
                {
                    status === STATUS_REFERRAL_CODES.ACTIVE && (
                        <>
                            <DropdownMenuItem className='hover:cursor-pointer' asChild>
                                <ActionDropdownItem icon={Ban} text={t('words_title.inactive')} onClick={handleClickInActive} />
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                        </>
                    )
                }

                {
                    status === STATUS_REFERRAL_CODES.IN_ACTIVE && (
                        <>
                            <DropdownMenuItem className='hover:cursor-pointer' asChild>
                                <ActionDropdownItem icon={Check} text={t('words_title.active')} onClick={handleClickActive} />
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                        </>
                    )
                }

                <DropdownMenuItem className='hover:cursor-pointer' asChild>
                    <ActionDropdownItem icon={Edit} text={t('words_title.edit_action')} onClick={handleClickEdit} />
                </DropdownMenuItem>
                <DropdownMenuSeparator />

                <DropdownMenuItem className='hover:cursor-pointer' asChild>
                    <ActionDropdownItem icon={Trash2Icon} text={t('words_title.delete_action')} onClick={handleClickDelete} />
                </DropdownMenuItem>

            </DropdownMenuContent>
        </DropdownMenu>
    );
});
